import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", contact: "", notes: "" };

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/suppliers");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/suppliers/${editing.id}`, form);
        toast.success("Supplier updated");
      } else {
        await api.post("/suppliers", form);
        toast.success("Supplier added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"?`)) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Supplier deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 max-w-6xl">
      <PageHeader
        eyebrow="Vendors"
        title="Suppliers"
        description="Maintain a list of vendors. Used in the supplier dropdown when adding parts."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} data-testid="add-supplier-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                <Plus className="w-4 h-4 mr-2" /> Add Supplier
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl tracking-tight">
                  {editing ? "Edit supplier" : "New supplier"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={onSave} className="space-y-4" data-testid="supplier-form">
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" className="rounded-sm border-zinc-300" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Contact (phone/email)</Label>
                  <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="supplier-contact-input" className="rounded-sm border-zinc-300" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm border-zinc-300" rows={2} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button type="submit" disabled={saving} data-testid="supplier-save-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                    {saving ? "Saving..." : editing ? "Save changes" : "Add supplier"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mt-6 bg-white border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              {["Name", "Contact", "Notes", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {loading && (<tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-zinc-500">Loading...</td></tr>)}
            {!loading && items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-zinc-500">No suppliers yet. Click "Add Supplier" to start.</td></tr>
            )}
            {items.map((s) => (
              <tr key={s.id} className="hover:bg-zinc-50" data-testid={`supplier-row-${s.id}`}>
                <td className="px-4 py-3 text-sm font-semibold text-zinc-950">{s.name}</td>
                <td className="px-4 py-3 text-sm text-zinc-700 font-mono">{s.contact || "—"}</td>
                <td className="px-4 py-3 text-sm text-zinc-700 truncate max-w-xs">{s.notes || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`} className="text-zinc-500 hover:text-zinc-950 mr-2"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => onDelete(s)} data-testid={`delete-supplier-${s.id}`} className="text-zinc-500 hover:text-[#FF2400]"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
